import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useMediaQuery } from "@mui/material";
import { theme } from "@/theme";
import { DroppsProduct, ScentDetails, productSelect } from "@/store";
import CartFlow from "@/features/pdp/CartFlow";
import { ProductHighlight } from "@/features/pdp/ProductHighLevel";
import {
  PdpProvider,
  usePdpFocusedProduct,
  usePdpProduct,
  usePdpScents,
} from "@/features/pdp/PdpContext";

import { MediaGallery } from "@/features/pdp/media-gallery";

import {
  FeatureBanner,
  FeatureBannerProps,
} from "@/features/pdp/FeatureBanner";
import { WhatsIncluded } from "@/features/pdp/WhatsIncluded";
import { PageModule } from "@/components/PageModule";
import styled from "@emotion/styled";
import { ProductBadge } from "@/features/product/ProductBadge";
import { Accordion } from "@/components/Accordion";
import { ProductRouter } from "@/features/pdp/ProductRouter";
import { StickyAddToCart } from "@/features/pdp/StickyAddToCart";
import { FragranceSection } from "../features/pdp/FragranceSection";

type PdpMetaData = FeatureBannerProps & {
  productSizeDetail: string;
};

const PdpImageContainer = styled.div`
  height: 100%;
  position: relative;

  img {
    /* max-height: 588px; */
    max-height: fit-content; // This is copied from Custom CSS in Shopify Admin
    aspect-ratio: 1;
  }
`;

export interface PdpFlowProps {
  product: DroppsProduct;
  scents: ScentDetails[];
  cartAddId: string;
  pdpMetaData: PdpMetaData;
  updateScentUrl: boolean;
}

export const PdpBadge = ({ index = 0 }: { index?: number }) => {
  const { badge } = usePdpFocusedProduct();

  return (
    (badge &&
      (badge.text ? (
        <ProductBadge text={badge.text} size="lg" />
      ) : (
        index === 0 && <ProductBadge {...badge} size="lg" />
      ))) ||
    null
  );
};

const PdpFragnanceSection = () => {
  /**
   * Fragnance section is sharing same provider.
   * In this component we are craeting a portal and rendering the ingredient component.
   * The React script is before content_for_layout in the theme.liquid we need a little delay to find the targeted elemnt to mount the ingredient component.
   */
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const scents = usePdpScents();
  const availableScents = scents.filter(
    (scent) => scent.isFragnanceSectionEnable
  );

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const findPortalTarget = () => {
      const targetElement = document.getElementById("pdp-fragnance-section");
      if (targetElement) {
        setPortalTarget(targetElement);
      } else {
        // Retry finding the portal target after a short delay
        timeoutId = setTimeout(findPortalTarget, 100);
      }
    };

    findPortalTarget();

    return () => {
      // Cleanup timeout when component unmounts
      clearTimeout(timeoutId);
    };
  }, []); // Empty dependency array ensures this runs only once
  return portalTarget && availableScents.length > 0
    ? ReactDOM.createPortal(
        <FragranceSection scents={availableScents} />,
        portalTarget
      )
    : null;
};

export function PdpFlow({
  product,
  scents,
  cartAddId,
  pdpMetaData,
  updateScentUrl,
}: PdpFlowProps) {
  // This hack is to fix the CLS issue on PDP
  const pdpHeaderElement = document.querySelector(".js-pdp-header");

  if (pdpHeaderElement) {
    pdpHeaderElement.classList.remove("lg:min-h-[1100px]");
  }

  return (
    <PdpProvider product={product} scents={scents}>
      {updateScentUrl ? <ProductRouter /> : null}
      <PageModule className="max-w-full px-0 py-0 lg:max-w-screen-xl lg:py-12">
        <ProductHero pdpMetaData={pdpMetaData} cartAddId={cartAddId} />
      </PageModule>
      {pdpMetaData.features && pdpMetaData.features.length > 0 && (
        <FeatureBanner features={pdpMetaData.features} />
      )}
      {updateScentUrl ? (
        <>
          <StickyAddToCart />
        </>
      ) : null}
      <PdpFragnanceSection />
    </PdpProvider>
  );
}

const IncludedAccordion = () => {
  const { bundledItems, overrideFutureShipments } = usePdpProduct();
  if (bundledItems === undefined || bundledItems.length === 0) {
    return null;
  }

  return (
    <>
      <hr className="divider-x my-0" />
      <Accordion
        entries={[
          {
            title: "What's Included",
            content: (
              <WhatsIncluded
                bundledItems={bundledItems}
                overrideFutureShipments={overrideFutureShipments}
              />
            ),
          },
        ]}
        defaultExpanded={[0]}
      />
    </>
  );
};

function ProductHero({
  pdpMetaData,
  cartAddId,
}: {
  pdpMetaData: PdpMetaData;
  cartAddId: string;
}) {
  const isMobile = useMediaQuery(
    theme.breakpoints.down(theme.breakpoints.values.md)
  );
  const product = usePdpFocusedProduct();
  const highlight = productSelect.highlight(product);
  const hideReviews = product.tags.includes("hide-reviews");

  return (
    <div className="pdp-flow__wrapper flex justify-between">
      <div className="pdp-buybox flex flex-col flex-nowrap pb-8 lg:sticky lg:top-12 lg:min-h-[588px] lg:self-start lg:pb-0">
        <PdpImageContainer>
          <MediaGallery mode={isMobile ? "carousel" : "thumbnail"} />
        </PdpImageContainer>
      </div>

      <div className="pdp-buybox-desc flex px-4 md:px-6 lg:justify-center">
        <div className="pdp-buybox-desc__inner w-full basis-full lg:w-auto">
          <ProductHighlight
            productDepartment={product.type}
            productName={productSelect.name(product)}
            productScent={productSelect.scent(product)}
            productLevelDescription={pdpMetaData.productSizeDetail}
            productHighlight={highlight}
            productId={product.id}
            hideReviews={hideReviews}
          />
          <CartFlow cartAddId={cartAddId} />
          <IncludedAccordion />
        </div>
      </div>
    </div>
  );
}

const IngredientsSyncer = ({ scents }: { scents: ScentDetails[] }) => {
  useSyncIngredientsInfo(scents);
  return null;
};

function useSyncIngredientsInfo(scents: ScentDetails[]) {
  const product = usePdpProduct();
  const selectedScent = scents.find(
    (x) => x.name == productSelect.scent(product)
  );

  useEffect(() => {
    if (!selectedScent) {
      return;
    }
    const ingredientsContainer = document.getElementById(
      "shopify-section-product-ingredients"
    );
    const title = ingredientsContainer?.querySelector(".accordion:last-child");
    const ingredientsPanel =
      ingredientsContainer?.querySelector(".panel:last-child");
    const ingredientList = ingredientsPanel?.querySelector(".article-content");
    const disclosureUrl = ingredientsContainer?.querySelector("a");

    if (ingredientList && selectedScent.ingredients?.contents) {
      ingredientList.textContent = selectedScent.ingredients.contents;
    }

    if (disclosureUrl && selectedScent.ingredients?.disclosureUrl) {
      disclosureUrl.href = selectedScent.ingredients.disclosureUrl;
    }

    if (title && selectedScent.ingredients?.title) {
      title.textContent = selectedScent.ingredients.title;
    }
  }, [selectedScent]);
}

export default PdpFlow;
